import { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  HStack,
  VStack,
  Heading,
  Grid,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Badge,
  Tabs,
  TabList,
  Tab,
  CardHeader,
  useToast,
  Spinner,
  Center,
} from "@chakra-ui/react";
import {
  Search,
  Funnel,
  MoreVertical,
  Calendar,
  Plus,
  LayoutGrid,
  List,
  Edit2,
  Trash2,
} from "lucide-react";

import { AdminLayout } from "@/components/layout/AdminLayout";
import NewPromotionModal from "@/components/modals/NewPromoModal";
import EditPromotionModal from "@/components/modals/EditPromoModal";
import { useAccessCheck } from "@/hooks/useAccess";
import PromoStatsCard from "@/components/StatsCard/PromoStatsCard";
import { promoService, PromoResponse, CreatePromoRequest, UpdatePromoRequest } from "@/lib/services/promoService";
import { useAuthStore } from "@/store/auth";

interface Promotion {
  id: number;
  header: string;
  body: string;
  startDate: string;
  endDate: string;
  status: "draft" | "ongoing" | "upcoming" | "expired";
  recipients: "all" | "specific";
  clientList?: string[];
  backendStatus: 0 | 1;
  originalData: PromoResponse; 
}

export default function NotificationsPage() {  
  const role = useAuthStore((state) => state.user?.role);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isGrid, setIsGrid] = useState(1);
  
  const { 
    isOpen: isNewOpen, 
    onOpen: onNewOpen, 
    onClose: onNewClose 
  } = useDisclosure();

  const { 
    isOpen: isEditOpen, 
    onOpen: onEditOpen, 
    onClose: onEditClose 
  } = useDisclosure();

  const searchRef = useRef<HTMLInputElement>(null);
  const access = useAccessCheck();
  const toast = useToast();

  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [selectedPromo, setSelectedPromo] = useState<PromoResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    ongoing: 0,
    upcoming: 0,
    drafts: 0,
    expired: 0,
  });

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
    }),
    [access],
  );

  const convertPromoResponse = (promo: PromoResponse): Promotion => {
    return {
      id: promo.id,
      header: promo.promo_header,
      body: promo.promo_body,
      startDate: promo.promo_duration_start,
      endDate: promo.promo_duration_end,
      status: promo.current_status, 
      recipients: promo.sent_type,
      clientList: promo.client_list ? promo.client_list.split(',').map(id => id.trim()) : undefined,
      backendStatus: promo.status,
      originalData: promo,
    };
  };

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    setIsLoading(true);
    try {
      const response = await promoService.getAllPromos();
      const converted = response.map(convertPromoResponse);
      setPromotions(converted);
      
      const statsData = await promoService.getStats();
      setStats({
        ongoing: statsData.ongoing,
        upcoming: statsData.upcoming,
        drafts: statsData.drafts,
        expired: statsData.expired,
      });
    } catch (error) {
      toast({
        title: "Error loading promotions",
        description: "Failed to fetch promotions from server",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    const query = searchRef.current?.value || "";
    setSearchTerm(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const filteredPromotions = useMemo(() => {
    const filtered = promotions.filter((promo) => {
      const matchesSearch =
        promo.header.toLowerCase().includes(searchTerm.toLowerCase()) ||
        promo.body.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter =
        filterStatus === "all" || promo.status === filterStatus;
      return matchesSearch && matchesFilter;
    });

    return filtered.sort((a, b) => {
      const statusPriority: Record<string, number> = {
        ongoing: 0,
        upcoming: 1,
        draft: 2,
        expired: 3,
      };

      const priorityA = statusPriority[a.status] ?? 99;
      const priorityB = statusPriority[b.status] ?? 99;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });
  }, [promotions, searchTerm, filterStatus]);

  const handleSaveAsDraft = async (data: any) => {
    try {
      const requestData: CreatePromoRequest = {
        promo_header: data.header,
        promo_body: data.body,
        promo_duration_start: data.startDate,
        promo_duration_end: data.endDate,
        is_draft: true,
        send_to_all: data.sendToAll,
        client_list: data.sendToAll ? undefined : data.clientList?.join(', '),
      };

      await promoService.createPromo(requestData);
      
      toast({
        title: "Promotion saved as draft",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      await loadPromotions();
      onNewClose();
    } catch (error) {
      toast({
        title: "Error saving draft",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreatePromotion = async (data: any) => {
    try {
      const requestData: CreatePromoRequest = {
        promo_header: data.header,
        promo_body: data.body,
        promo_duration_start: data.startDate,
        promo_duration_end: data.endDate,
        is_draft: false,
        send_to_all: data.sendToAll,
        client_list: data.sendToAll ? undefined : data.clientList?.join(', '),
      };

      await promoService.createPromo(requestData);
      
      toast({
        title: "Promotion created",
        description: "Your promotion has been created successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      await loadPromotions();
      onNewClose();
    } catch (error) {
      toast({
        title: "Error creating promotion",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdatePromotion = async (id: number, data: UpdatePromoRequest) => {
    try {
        await promoService.updatePromo(id, data);
        
        toast({
            title: "Promotion updated",
            description: "Promotion details have been updated successfully.",
            status: "success",
            duration: 3000,
            isClosable: true,
        });

        await loadPromotions();
        onEditClose();
    } catch (error) {
        toast({
            title: "Error updating promotion",
            description: "Failed to update promotion details.",
            status: "error",
            duration: 3000,
            isClosable: true,
        });
    }
  };

  const handleDeletePromotion = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this promotion?")) {
      return;
    }

    try {
      await promoService.deletePromo(id);
      
      toast({
        title: "Promotion deleted",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      await loadPromotions();
    } catch (error) {
      toast({
        title: "Error deleting promotion",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEditPromotion = (promo: Promotion) => {
    setSelectedPromo(promo.originalData);
    onEditOpen();
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "ongoing": return "red";
      case "upcoming": return "yellow";
      case "draft": return "blue";
      case "expired": return "gray";
      default: return "gray";
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Center h="100vh">
          <Spinner size="xl" color="#DC3611" />
        </Center>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box minH="calc(100vh - 110px)"  overflow="hidden" display="flex" flexDirection="column" bg="#FEFCF8" py={1} px={5}>        
        <VStack spacing={2} align="stretch">
          <Card flexShrink={0} bg="transparent" boxShadow="none" border="none">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={0} ml={-4}>
                Notifications and Promo
              </Heading>
            </CardHeader>
          </Card>
          <Box>
            {(role === "Developer Account" || role === "Web Admin") && (
              <PromoStatsCard stats={stats} />
            )}

            <Flex
              direction={{ base: "column", lg: "row" }}
              justify="space-between"
              align={{ base: "flex-start", lg: "center" }}
              gap={{ base: 3, lg: 0 }}
              mb={3}
            >
              <Box>
                <Badge
                  textTransform="none"
                  className="rounded-sm border-[#2E37A4] border"
                  py="4px"
                  px="8px"
                  color="#2E37A4"
                  fontWeight="500"
                  fontSize={{ base: "2xs", lg: "xs" }}
                  bg="blue.50"
                >
                  TOTAL PROMOTIONS: {filteredPromotions.length}{" "}
                  {searchTerm && `of ${promotions.length}`}
                </Badge>
              </Box>

              <Flex
                py={1}
                pt={2}
                pb={2}
                shrink={0}
                justify="space-between"
                direction={{ base: "column", lg: "row" }}
              >
                <HStack
                  spacing={3}
                  flexWrap="wrap"
                  h="auto"
                  w="100%"
                  align="center"
                  justify={{ base: "space-between", sm: "flex-start" }}
                >
                  <Box
                    flex={{ base: "1", sm: "0 1 auto" }}
                    minW={{ base: "120px", sm: "150px", md: "180px" }}
                    maxW={{ base: "180px", md: "200px" }}
                    h="32px"
                    bg="white"
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    order={{ base: 1, sm: 1 }}
                  >
                    <InputGroup size="lg" h="full">
                      <InputLeftElement pointerEvents="none" h="full">
                        <Search size={14} color="gray" />
                      </InputLeftElement>
                      <Input
                        ref={searchRef}
                        placeholder="Search "
                        defaultValue={searchTerm}
                        onChange={handleSearch}
                        _hover={{ borderColor: "#DC3611" }}
                        _focus={{
                          borderColor: "#DC3611",
                          boxShadow: "0 0 0 1px #DC3611",
                        }}
                        gap="8px"
                        onKeyDown={handleKeyDown}
                        fontSize={{ base: "sm", lg: "md" }}
                        h="full"
                      />
                    </InputGroup>
                  </Box>

                  <Menu>
                    <MenuButton
                      py="6px"
                      px="12px"
                      as={Button}
                      variant="outline"
                      fontSize={{ base: "xs", lg: "sm" }}
                      h="32px"
                      leftIcon={<Funnel size={12} />}
                      display={{ base: "none", sm: "flex" }}
                      order={{ base: 2, sm: 2 }}
                      bg="white"
                    >
                      Filter
                    </MenuButton>
                    <MenuList fontSize="sm">
                      <MenuItem onClick={() => setFilterStatus("all")}>All Promotions</MenuItem>
                      <MenuItem onClick={() => setFilterStatus("ongoing")}>On-going</MenuItem>
                      <MenuItem onClick={() => setFilterStatus("upcoming")}>Up Coming</MenuItem>
                      <MenuItem onClick={() => setFilterStatus("draft")}>Drafts</MenuItem>
                      <MenuItem onClick={() => setFilterStatus("expired")}>Expired</MenuItem>
                    </MenuList>
                  </Menu>

                  <Tabs variant="unstyled" h="32px" order={{ base: 3, sm: 3 }}>
                    <TabList className="rounded-md border flex items-center" h="32px">
                      <Tab className="w-8 h-8" onClick={() => setIsGrid(0)} p={0} display="flex" alignItems="center" justifyContent="center">
                        <Box className={`p-1 rounded-sm ${!isGrid ? "bg-gray-400/20" : "bg-white"}`}>
                          <List size={12} />
                        </Box>
                      </Tab>
                      <Tab className="w-8 h-8" onClick={() => setIsGrid(1)} p={0} display="flex" alignItems="center" justifyContent="center">
                        <Box className={`p-1 rounded-sm text-black ${isGrid ? "bg-gray-400/20" : "bg-white"}`}>
                          <LayoutGrid size={12} />
                        </Box>
                      </Tab>
                    </TabList>
                  </Tabs>

                  {permissions.can_create && (
                    <>
                      <IconButton
                        aria-label="New Promo"
                        icon={<Plus size={16} />}
                        bg="red.500"
                        _hover={{ bg: "#bf3c1fff" }}
                        color="white"
                        size="sm"
                        h="32px"
                        w="32px"
                        onClick={onNewOpen}
                        display={{ base: "flex", sm: "none" }}
                        order={{ base: 2, sm: 4 }}
                        minW="32px"
                      />
                      <Button
                        py="6px"
                        px="10px"
                        bg="red.500"
                        _hover={{ bg: "#bf3c1fff" }}
                        colorScheme="blue"
                        fontSize={{ base: "xs", lg: "sm" }}
                        leftIcon={<Plus size={12} />}
                        h="32px"
                        onClick={onNewOpen}
                        display={{ base: "none", sm: "flex" }}
                        order={{ base: 4, sm: 4 }}
                        flexShrink={0}
                        whiteSpace="nowrap"
                      >
                        New Promo
                      </Button>
                    </>
                  )}
                </HStack>
              </Flex>
            </Flex>

            <Box>
              {filteredPromotions.length === 0 ? (
                <Flex
                  justify="center"
                  align="center"
                  minH="200px"
                  bg="white"
                  borderRadius="12px"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Text color="gray.500" fontSize="sm">
                    {searchTerm
                      ? `No promotions found matching "${searchTerm}"`
                      : "No promotions found"}
                  </Text>
                </Flex>
              ) : (
                <Box>
                  {isGrid ? (
                    <Grid
                    templateColumns={{
                      base: "1fr",
                      md: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    }}
                    gap={4}
                    pb={4}
                  >
                    {filteredPromotions.map((promo) => {
                      const isUpcoming = new Date(promo.startDate) > new Date();
                      const isExpired = new Date(promo.endDate) < new Date();
                      const isOngoing = !isUpcoming && !isExpired;

                      let progressPercent = 0;
                      if (isOngoing) {
                        const totalDuration =
                          new Date(promo.endDate).getTime() -
                          new Date(promo.startDate).getTime();
                        const elapsed =
                          new Date().getTime() -
                          new Date(promo.startDate).getTime();
                        progressPercent = Math.min(
                          100,
                          Math.max(0, (elapsed / totalDuration) * 100)
                        );
                      } else if (isExpired) {
                        progressPercent = 100;
                      }

                      let dateLabel = "";
                      let dateValue = "";
                      
                      if (isUpcoming) {
                        dateLabel = "Start Date:";
                        dateValue = new Date(promo.startDate).toLocaleDateString("en-US", { day: 'numeric', month: 'short', year: 'numeric' });
                      } else if (isOngoing) {
                        dateLabel = "End Date:";
                        dateValue = new Date(promo.endDate).toLocaleDateString("en-US", { day: 'numeric', month: 'short', year: 'numeric' });
                      } else {
                        dateLabel = "Ended:";
                        dateValue = new Date(promo.endDate).toLocaleDateString("en-US", { day: 'numeric', month: 'short', year: 'numeric' });
                      }

                      return (
                        <Card
                          key={promo.id}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="12px"
                          shadow="sm"
                          _hover={{ shadow: "md", transform: "translateY(-2px)" }}
                          transition="all 0.2s"
                          overflow="hidden"
                          display="flex"
                          flexDirection="column"
                          h="100%"
                        >
                          <CardBody p={5} flex="1" display="flex" flexDirection="column">
                            <Flex justify="space-between" align="flex-start" mb={2}>
                              <Heading size="md" color="#1A202C" fontWeight="700" lineHeight="shorter" mr={2}>
                                {promo.header}
                              </Heading>
                              <Menu>
                                <MenuButton
                                  as={IconButton}
                                  icon={<MoreVertical size={18} />}
                                  variant="ghost"
                                  size="xs"
                                  color="gray.400"
                                  aria-label="Options"
                                  _hover={{ bg: "gray.100", color: "gray.600" }}
                                />
                                <MenuList fontSize="sm">
                                  {permissions.can_edit && (
                                    <MenuItem icon={<Edit2 size={14} />} onClick={() => handleEditPromotion(promo)}>
                                      Edit
                                    </MenuItem>
                                  )}
                                  {permissions.can_delete && (
                                    <MenuItem icon={<Trash2 size={14} />} color="red.500" onClick={() => handleDeletePromotion(promo.id)}>
                                      Delete
                                    </MenuItem>
                                  )}
                                </MenuList>
                              </Menu>
                            </Flex>

                            <Text fontSize="sm" color="gray.500" noOfLines={3} lineHeight="1.6" flex="1" mb={6}>
                              {promo.body}
                            </Text>

                            <Box mt="auto">
                              <HStack spacing={2} mb={2} color="gray.500">
                                <Calendar size={14} />
                                <Text fontSize="xs" fontWeight="600">
                                  {dateLabel} {dateValue}
                                </Text>
                              </HStack>

                              {promo.status !== "draft" && (
                                <Box w="100%" h="6px" bg="red.100" borderRadius="full" overflow="hidden">
                                  <Box w={`${progressPercent}%`} h="100%" bg="#DC3611" borderRadius="full" transition="width 0.4s ease-in-out" />
                                </Box>
                              )}
                              {promo.status === "draft" && <Badge colorScheme="gray">Draft</Badge>}
                            </Box>
                          </CardBody>
                        </Card>
                      );
                    })}
                  </Grid>
                  ) : (
                    /* List View */
                    <Box borderRadius="lg" border="1px" borderColor="gray.200" overflow="hidden" bg="white">
                      <Flex align="center" gap={{ base: 2, md: 4 }} borderBottom="1px" borderColor="gray.200" py={2} px={{ base: 3, md: 6 }} bg="white" fontWeight="600" fontSize="sm" color="gray.700" display={{ base: "none", md: "flex" }}>
                        <Box flex={2} minW="200px">Promotion</Box>
                        <Box flex={1.5} minW="150px">Start Date</Box>
                        <Box flex={1.5} minW="150px">End Date</Box>
                        <Box flex={1} minW="100px">Status</Box>
                        <Box w="50px"></Box>
                      </Flex>

                      <Box>
                        {filteredPromotions.map((promo) => (
                          <Flex key={promo.id} align="center" gap={{ base: 2, md: 4 }} borderBottom="1px" borderColor="gray.200" py={3} px={{ base: 3, md: 6 }} _hover={{ bg: "gray.50" }} cursor="pointer">
                            <Box flex={2} minW="200px">
                              <Text fontSize="sm" fontWeight="500" color="#0A1B39">{promo.header}</Text>
                              <Text fontSize="xs" color="gray.600" noOfLines={1}>{promo.body}</Text>
                            </Box>
                            <Box flex={1.5} minW="150px">
                              <Text fontSize="xs" color="gray.600">{new Date(promo.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</Text>
                            </Box>
                            <Box flex={1.5} minW="150px">
                              <Text fontSize="xs" color="gray.600">{new Date(promo.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</Text>
                            </Box>
                            <Box flex={1} minW="100px">
                              <Badge colorScheme={getStatusBadgeColor(promo.status)} fontSize="2xs" variant="outline" borderRadius="md" px={2} py={1}>{promo.status}</Badge>
                            </Box>
                            <Box w="50px">
                              <Menu>
                                <MenuButton as={IconButton} icon={<MoreVertical size={16} />} variant="ghost" size="sm" aria-label="Options" />
                                <MenuList fontSize="sm">
                                  {permissions.can_edit && <MenuItem icon={<Edit2 size={14} />} onClick={() => handleEditPromotion(promo)}>Edit</MenuItem>}
                                  {permissions.can_delete && <MenuItem color="red.500" onClick={() => handleDeletePromotion(promo.id)}>Delete</MenuItem>}
                                </MenuList>
                              </Menu>
                            </Box>
                          </Flex>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </VStack>
      </Box>

      {/* New Promotion Modal */}
      <NewPromotionModal
        isOpen={isNewOpen}
        onClose={onNewClose}
        onSave={handleCreatePromotion}
        onSaveAsDraft={handleSaveAsDraft} 
      />

      {/* Edit Promotion Modal */}
      <EditPromotionModal
        isOpen={isEditOpen}
        onClose={onEditClose}
        onUpdate={handleUpdatePromotion}
        promotion={selectedPromo}
      />
    </AdminLayout>
  );
}